import { diffWords } from "./textDiff";

// Turns the comment history API's versions (newest first: { comments,
// attachments: [{id, filename}], by, time, current }) into timeline entries that
// say in plain words what each edit changed.
//
// Mentions are stored as plain "@Name" text, so they're recognised by matching
// "@" + a known employee name (longest names first). Text that isn't a known
// name is treated as ordinary text, never guessed to be a mention.

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const mentionMatcher = (names = []) => {
  const list = [...new Set(names.filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!list.length) return null;
  const alternatives = list.map(escapeRe).join("|");
  try {
    // "@" must start a word, so an email address is not a mention.
    return new RegExp(`(?<![\\w@])@(${alternatives})(?![\\w])`, "g");
  } catch {
    // Browsers without lookbehind (old Safari): accept any "@Name".
    return new RegExp(`@(${alternatives})(?![\\w])`, "g");
  }
};

export const mentionsIn = (text, matcher) => {
  if (!matcher || !text) return [];
  return [...new Set([...text.matchAll(new RegExp(matcher.source, "g"))].map(m => m[1]))];
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const clip = (s, n = 80) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

const listWithAnd = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);

/**
 * versions: newest first. toText: version content → plain text (HTML comments).
 * Returns entries newest first:
 *   { key, by, time, isLatest, isOriginal, title, types:Set<"text"|"mention"|"files">,
 *     runs, before, after, addedText, removedText, mentionsAdded, mentionsRemoved,
 *     filesAdded, filesRemoved, attachments }
 */
export const buildHistoryEntries = (versions = [], { toText = (s) => s || "", employeeNames = [] } = {}) => {
  const matcher = mentionMatcher(employeeNames);
  return versions.map((v, i) => {
    const older = versions[i + 1];
    const base = { key: `v${versions.length - i}`, by: v.by, time: v.time, isLatest: i === 0 && versions.length > 1, isOriginal: !older, attachments: v.attachments || [] };
    if (!older) {
      return { ...base, title: "Created comment", types: new Set(), after: toText(v.comments), before: "", runs: [] };
    }

    const before = toText(older.comments);
    const after = toText(v.comments);
    const runs = diffWords(before, after);
    const mentionsBefore = mentionsIn(before, matcher);
    const mentionsAfter = mentionsIn(after, matcher);
    const mentionsAdded = mentionsAfter.filter(m => !mentionsBefore.includes(m));
    const mentionsRemoved = mentionsBefore.filter(m => !mentionsAfter.includes(m));

    // Added / removed words, minus the mentions (reported on their own).
    const stripMentions = (s) => (matcher ? s.replace(new RegExp(matcher.source, "g"), " ") : s);
    const joinRuns = (type) => runs.filter(r => r.type === type).map(r => r.text).join(" ").replace(/\s+/g, " ").trim();
    const addedText = stripMentions(joinRuns("add")).replace(/\s+/g, " ").trim();
    const removedText = stripMentions(joinRuns("del")).replace(/\s+/g, " ").trim();

    const oldIds = new Set((older.attachments || []).map(a => a.id));
    const newIds = new Set((v.attachments || []).map(a => a.id));
    const filesAdded = (v.attachments || []).filter(a => !oldIds.has(a.id));
    const filesRemoved = (older.attachments || []).filter(a => !newIds.has(a.id));

    const types = new Set();
    if (addedText || removedText) types.add("text");
    if (mentionsAdded.length || mentionsRemoved.length) types.add("mention");
    if (filesAdded.length || filesRemoved.length) types.add("files");

    const parts = [];
    if (mentionsAdded.length) parts.push(`mentioned ${mentionsAdded.map(m => `@${m}`).join(", ")}`);
    if (mentionsRemoved.length) parts.push(`removed the mention of ${mentionsRemoved.map(m => `@${m}`).join(", ")}`);
    if (addedText && removedText) parts.push("edited the text");
    else if (addedText) parts.push("added text");
    else if (removedText) parts.push("removed text");
    if (filesAdded.length) parts.push(`added ${plural(filesAdded.length, "attachment")}`);
    if (filesRemoved.length) parts.push(`removed ${plural(filesRemoved.length, "attachment")}`);
    const sentence = parts.length ? listWithAnd(parts) : "saved without changes";

    return {
      ...base,
      title: sentence.charAt(0).toUpperCase() + sentence.slice(1),
      types, runs, before, after,
      addedText: clip(addedText), removedText: clip(removedText),
      mentionsAdded, mentionsRemoved, filesAdded, filesRemoved,
    };
  });
};

// Entries for a filter tab: "all", "text" (text or mentions), "files".
export const filterEntries = (entries, filter) => {
  if (filter === "text") return entries.filter(e => e.types.has("text") || e.types.has("mention"));
  if (filter === "files") return entries.filter(e => e.types.has("files"));
  return entries;
};
